import React, { useCallback, useEffect, useState } from 'react'
import { StepModel } from "./step.model";
import { Patient } from "./patient/patient";
import { Definitions } from "./definitions";
import { Actives } from "./topic/actives/actives";
import { SubClass } from "./topic/subclass/sub-class";
import { Products } from "./topic/products/products";
import { useLocation, useNavigate } from "react-router-dom";
import { ActiveResponse, getSubclasses, SubClassesObject, SubclassesResponse } from "../../../services/decisionFlow";
import { useAuth } from "../../../contexts/auth2";
import { MessageDialog } from "../../../components/message-dialog";
import { getAllActives } from "../../../services/activeService";
import VehiclesModel from "../../../models/vehicles.model";
import { getVehicles } from "../../../services/vehicles.service";
import { Vehicles } from "./topic/vehicles/vehicles";
import { getConsult } from "../../../services/history";
import { clearOralQuery, clearQuery, getQuery, setOralQuery, setQuery } from "../../../services/local-session-storage";
import { ConsultationModel } from "../../../domain/models/consultation.model";
import { toConsultation } from "./query.model";
import styled from "styled-components";
import { Box } from '@mui/material';
import { TOPBAR_DESKTOP, TOPBAR_MOBILE } from "../../../layouts/dashboard/DashboardNavbar";
import { ActivesOral } from './oral/activesOral/actives.oral';
import { FlowType } from "./flow-type";
import { handlePromise } from '../../../utils/handlePromisse';
import { OralModel } from '../../../models/Oral.model';
import { getConsultOral } from '../../../services/oralService';
import { toOralConsultation } from "./oral/activesOral/oral.query.model";

export type SubClassType = {
    active: string
    subClass: SubClassesObject[]
}

export type QueryState = {
    patientId?: string | number
    patientIds?: number[]
    consultationId?: string
    fromDoseNavigation?: boolean
    fromDoseOral?: boolean
    type?: FlowType
}

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Centraliza via "margin: auto" no filho (WizardShell): quando há espaço, o
     conteúdo fica centrado; quando transborda, as margens viram 0 e a página rola
     até o footer (Voltar/Continuar) — evita o footer cortado em telas baixas. */
  justify-content: flex-start;
  width: 100%;
  overflow-y: auto;
`


const Index: React.FC = () => {

    const model = getQuery()
    const { state } = useLocation();
    const navigate = useNavigate()
    const { user: { idUser } } = useAuth()
    const [open, setOpen] = useState(false)
    const [flowType, setFlowType] = useState<FlowType>('topic')
    const [currentStep, setCurrentStep] = useState<StepModel>(StepModel.PATIENT)
    const [actives, setActives] = useState<ActiveResponse[]>([])
    const [subclass, setSubclass] = useState<SubClassType[]>([])
    const [vehicles, setVehicles] = useState<VehiclesModel[]>([])

    const fetchActives = useCallback(async () => {
        const result = await getAllActives()
        const data = result.data.data as ActiveResponse[];
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));

        setActives(sortedData);
    }, [setActives])

    const fetchVehicles = useCallback(async () => {
        const result = await getVehicles()
        const data = result.data.data as VehiclesModel[];
        setVehicles(data)
    }, [setVehicles])

    const fetchSubclasses = useCallback(async (actives: ActiveResponse[]) => {
        const activeMap = actives.map(value => value.id)
        const result = await getSubclasses({ actives: activeMap })

        const data = result.data as SubclassesResponse;

        let normalizedList: SubClassType[] = []
        for (let attr in data) {
            normalizedList = normalizedList.concat({
                active: attr,
                subClass: data[attr]
            } as SubClassType)
        }
        if (normalizedList.length > 0) {
            toggleStep(StepModel.SUBCLASS)
        } else {
            toggleStep(StepModel.PRODUCTS)
        }
        setSubclass(normalizedList)
    }, [setSubclass])

    const fetchConsultation = useCallback(async (id: string) => {
        const result = await getConsult(Number(id))
        const consultation = result.data as ConsultationModel

        clearQuery()
        setQuery(toConsultation(consultation))
        setQuery({ canEdit: false })

        toggleStep(StepModel.PRODUCTS)
    }, [getConsult])

    const fetchOralConsultation = useCallback(async (id: string) => {
        const [result, _] = await handlePromise(getConsultOral(id))
        const consultation = result.data

        clearOralQuery()
        setOralQuery(toOralConsultation(
            consultation.name,
            consultation.patientId,
            consultation.observations,
            consultation.actives as OralModel[]),
        )

        setFlowType('oral')
        toggleStep(StepModel.ACTIVES_ORAL)
    }, [])

    const handleActiveList = useCallback(async () => {
        const result = await getAllActives()
        const ativos = result.data.data as ActiveResponse[]
        const data = getQuery()
        if (data.activeList) {
            setQuery({ actives: ativos })
            toggleStep(StepModel.PRODUCTS)
        } else {
            toggleStep(StepModel.ACTIVES)
        }
    }, [model.activeList])

    useEffect(() => {
        fetchActives()
        fetchVehicles()
        if (state !== null) {
            const query = (state as QueryState);
            if (query.consultationId !== undefined) {
                if (query.type == 'oral') {
                    fetchOralConsultation(query.consultationId)
                } else {
                    fetchConsultation(query.consultationId)
                }
            }
            if (query.fromDoseOral !== undefined) {
                toggleStep(StepModel.ACTIVES_ORAL)
                setFlowType('oral')
            }
            if (query.fromDoseNavigation !== undefined) {
                toggleStep(StepModel.VEHICLES)
            } else if (query.patientIds !== undefined) {
                setQuery({ patientIds: query.patientIds, canEdit: true })
                setOralQuery({ patientIds: query.patientIds })
                toggleStep(StepModel.DEFINITIONS)
            } else if (query.patientId !== undefined) {
                setQuery({ patientId: query.patientId, canEdit: true })
                setOralQuery({patient: query.patientId})
                toggleStep(StepModel.DEFINITIONS)
            }
        }
    }, [fetchActives, fetchVehicles,])

    const handleActivesFinish = (actives: ActiveResponse[]) => {
        fetchSubclasses(actives)
    }

    const toggleStep = useCallback((nextStep: StepModel) => {
        setCurrentStep(nextStep)
    }, [setCurrentStep])

    const goToDose = useCallback(() => {
        navigate('/dashboard/dose')
    }, [])

    const goToDoseOral = useCallback(() => {
        navigate('/dashboard/doseOral')
    }, [])

    const topicFlow = () => {
        return (
            <>
                {
                    actives && currentStep === StepModel.ACTIVES &&
                    <Actives
                        actives={actives}
                        onFinish={(actives) => handleActivesFinish(actives)}
                        onBack={() => toggleStep(StepModel.DEFINITIONS)}
                    />
                }
                {
                    subclass && currentStep === StepModel.SUBCLASS &&
                    <SubClass
                        subclass={subclass}
                        onFinish={() => toggleStep(StepModel.PRODUCTS)}
                        onBack={() => toggleStep(StepModel.ACTIVES)}
                    />
                }
                {
                    currentStep === StepModel.PRODUCTS &&
                    <Products
                        onFinish={() => toggleStep(StepModel.VEHICLES)}
                        onAddMoreItem={async () => {
                            return model.activeList
                                ? toggleStep(StepModel.DEFINITIONS)
                                : toggleStep(StepModel.ACTIVES)
                        }}
                    />
                }
                {
                    currentStep === StepModel.VEHICLES &&
                    <Vehicles
                        vehicles={vehicles}
                        onFinish={() => goToDose()}
                        onBack={() => toggleStep(StepModel.PRODUCTS)}
                    />
                }
            </>
        )
    }

    const oralFlow = () => {
        return (
            <>
                {
                    currentStep === StepModel.ACTIVES_ORAL &&
                    <ActivesOral
                        onBack={() => toggleStep(StepModel.DEFINITIONS_ORAL)}
                        onFinish={() => goToDoseOral()}
                    />
                }
            </>
        )
    }

    return (
        <Container sx={{ height: { xs: `calc(100vh - ${TOPBAR_MOBILE}px)`, lg: `calc(100vh - ${TOPBAR_DESKTOP}px)` } }}>
            <MessageDialog
                emoji="😢"
                open={open}
                title={"aaah, que pena! "}
                message={"Você excedeu o limite de consultas para seu plano, deseja fazer o upgrade?"}
                onClose={() => navigate('/dashboard/app')}
                onConfirm={() => navigate('/dashboard/pricing')}
                confirmButtonText={"Fazer Agora"}
                cancelButtonText={"Cancelar"}/>
            {
                currentStep === StepModel.PATIENT &&
                <Patient
                    onFinish={() => {
                        toggleStep(StepModel.DEFINITIONS)
                    }}
                    onCancel={() => navigate('/dashboard/app')}
                />
            }
            {
                (currentStep === StepModel.DEFINITIONS || currentStep === StepModel.DEFINITIONS_ORAL) &&
                <Definitions
                    flowType={flowType}
                    onFlowTypeChange={(type) => {
                        setFlowType(type)
                        setQuery({actives: []})
                    }}
                    onFinish={() => {
                        flowType === 'topic' ? handleActiveList() : toggleStep(StepModel.ACTIVES_ORAL)
                    }}
                    onBack={() => {
                        toggleStep(StepModel.PATIENT)
                    }}
                />
            }

            {flowType == 'oral' && oralFlow()}
            {flowType == 'topic' && topicFlow()}
        </Container>
    )
}

export default Index;
